import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CalendarBlank, CaretRight, Gavel } from '@liveauctioneers/hammer-ui-core/icons';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { getMonthDayYearTime } from '@liveauctioneers/hammer-ui-core/utils/dateTimeFormat';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import ms from 'ms';
import { useCallback, useMemo } from 'react';

import AutomatedCatalogCarouselCardProgressBar from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/components/ProgressBar/AutomatedCatalogCarouselCardProgressBar';
import { useTrackAndNavigateCatalogCardClick } from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/hooks/useTrackAndNavigateCatalogCardClick';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import {
    getCatalogSellerName,
    getCatalogStartTime,
    getCatalogTitle,
    getShouldShowLive,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogStatus } from '@/redux/modules/console';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getCatalogUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    className?: string;
};

const AutomatedCatalogCarouselCardCatalogInfo = ({ catalogId, className = '' }: Props) => {
    const isMobile = useIsMobileLayout();
    const formatMessage = useAppFormatMessage();

    const liveStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const shouldShowLive = useAppSelector((state) => getShouldShowLive(state, catalogId));
    const isLive = shouldShowLive || liveStatus === 'live';

    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const houseName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const startTime = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const catalogUrl = useMemo(() => getCatalogUrl(catalogId, title), [catalogId, title]);

    const trackCardClick = useTrackAndNavigateCatalogCardClick(catalogId);
    const onClickExplore = useCallback(() => {
        trackCardClick();
    }, [trackCardClick]);

    const datetime = useMemo(
        () =>
            new Date(
                // convert from seconds to milliseconds
                startTime * ms('1s')
            ).toISOString(),
        [startTime]
    );

    return (
        <div className={className}>
            <Link
                className="text-inherit no-underline outline-0 hover:no-underline"
                href={catalogUrl}
                onClick={(event) => {
                    event.stopPropagation();
                    onClickExplore();
                }}
            >
                <H2 className="ws-nowrap line-clamp-1 w-full overflow-hidden overflow-ellipsis text-text-inverse">
                    {title}
                </H2>
            </Link>

            <div className="mt-sm flex justify-between">
                <div
                    className={cn({
                        'w-[75%]': !isMobile,
                        'w-full': isMobile,
                    })}
                >
                    <div className="flex items-center gap-xs">
                        <Gavel
                            color="inverse"
                            size="xs"
                        />
                        <BodySecondary color="inverse">{houseName}</BodySecondary>
                    </div>
                    {isLive ? (
                        <div className="mt-xs">
                            <AutomatedCatalogCarouselCardProgressBar catalogId={catalogId} />
                        </div>
                    ) : (
                        <div className="mt-xs flex items-center gap-xs">
                            <CalendarBlank
                                color="inverse"
                                size="xs"
                            />
                            <time
                                className="hui-text-body-secondary text-text-inverse"
                                dateTime={datetime}
                            >
                                {getMonthDayYearTime(startTime)}
                            </time>
                        </div>
                    )}
                </div>
                {!isMobile && (
                    <div>
                        <Button
                            className="mt-sm"
                            iconRight={CaretRight}
                            onClick={(event) => {
                                event.stopPropagation();
                                onClickExplore();
                            }}
                            size="sm"
                            style="inverse"
                            variant="outlined"
                        >
                            {formatMessage(['featuredAuctions', 'explore'])}
                        </Button>
                    </div>
                )}
            </div>
            {isMobile && (
                <Button
                    className="mt-sm w-full"
                    onClick={(event) => {
                        event.stopPropagation();
                        onClickExplore();
                    }}
                    size="sm"
                    style="inverse"
                >
                    {formatMessage(['featuredAuctions', 'exploreAuction'])}
                </Button>
            )}
        </div>
    );
};

export default AutomatedCatalogCarouselCardCatalogInfo;
